import { useEffect, useState, useCallback } from "react";
import { api, formatRupiah } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { CurrencyInput } from "@/components/CurrencyInput";
import { toast } from "sonner";
import { ArrowLeftRight, Plus, Trash2, ArrowRight } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-indigo-500 transition";

const emptyLine = { inventory_id: "", nama: "", value: 0, qty: 1 };

function LineEditor({ title, lines, setLines, inventory, testid }) {
  const add = () => setLines((prev) => [...prev, { ...emptyLine }]);
  const upd = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const rem = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/30 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading font-semibold text-white text-sm">{title}</h4>
        <button onClick={add} data-testid={`${testid}-add`} className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"><Plus className="w-3.5 h-3.5" /> Tambah kartu</button>
      </div>
      <div className="space-y-2">
        {lines.length === 0 && <p className="text-xs text-slate-600 text-center py-3">Belum ada kartu.</p>}
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <select
              value={l.inventory_id}
              onChange={(e) => {
                const inv = inventory.find((x) => x.id === e.target.value);
                upd(i, inv ? { inventory_id: e.target.value, nama: inv.nama_produk, value: inv.harga_jual } : { inventory_id: e.target.value });
              }}
              className={`${inputCls} col-span-3`}
              data-testid={`${testid}-inv-${i}`}
            >
              <option value="">— Manual —</option>
              {inventory.map((it) => <option key={it.id} value={it.id}>{it.nama_produk} (stok {it.stok_qty})</option>)}
            </select>
            <input value={l.nama} onChange={(e) => upd(i, { nama: e.target.value })} placeholder="Nama kartu" className={`${inputCls} col-span-3`} />
            <input type="number" inputMode="numeric" value={l.qty} onChange={(e) => upd(i, { qty: e.target.value })} placeholder="Qty" className={`${inputCls} col-span-2 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`} title="Qty" />
            <div className="col-span-3"><CurrencyInput value={l.value} onChange={(v) => upd(i, { value: v })} className={inputCls} placeholder="Value" /></div>
            <button onClick={() => rem(i)} className="col-span-1 text-slate-500 hover:text-rose-400 flex justify-center"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Trades() {
  const { isAdmin } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [events, setEvents] = useState([]);
  const [trades, setTrades] = useState([]);
  const [namaLawan, setNamaLawan] = useState("");
  const [kontak, setKontak] = useState("");
  const [eventId, setEventId] = useState("");
  const [keluar, setKeluar] = useState([]);
  const [masuk, setMasuk] = useState([]);
  const [cashKeluar, setCashKeluar] = useState(0);
  const [cashMasuk, setCashMasuk] = useState(0);
  const [catatan, setCatatan] = useState("");
  const [saving, setSaving] = useState(false);

  const loadInv = useCallback(async () => { const { data } = await api.get("/inventory"); setInventory(data); }, []);
  const loadTrades = useCallback(async () => { const { data } = await api.get("/trades"); setTrades(data); }, []);
  const loadEvents = useCallback(async () => { const { data } = await api.get("/events"); setEvents(data); }, []);

  useEffect(() => { loadInv(); loadTrades(); loadEvents(); }, [loadInv, loadTrades, loadEvents]);

  const nilaiKeluar = keluar.reduce((a, b) => a + (Number(b.value) || 0) * (Number(b.qty) || 0), 0) + Number(cashKeluar || 0);
  const nilaiMasuk = masuk.reduce((a, b) => a + (Number(b.value) || 0) * (Number(b.qty) || 0), 0) + Number(cashMasuk || 0);
  const selisih = nilaiMasuk - nilaiKeluar;

  const submit = async () => {
    if (!namaLawan.trim()) return toast.error("Nama lawan trade wajib diisi");
    if (keluar.length === 0 && masuk.length === 0) return toast.error("Isi minimal satu kartu keluar/masuk");
    setSaving(true);
    const sane = (arr) => arr.map((l) => ({ ...l, qty: Number(l.qty) || 1, value: Number(l.value) || 0 }));
    try {
      await api.post("/trades", {
        nama_lawan: namaLawan, kontak, event_id: eventId || null,
        barang_keluar: sane(keluar), barang_masuk: sane(masuk),
        cash_keluar: Number(cashKeluar || 0), cash_masuk: Number(cashMasuk || 0),
        catatan,
      });
      toast.success("Trade tercatat");
      setNamaLawan(""); setKontak(""); setEventId(""); setKeluar([]); setMasuk([]); setCashKeluar(0); setCashMasuk(0); setCatatan("");
      loadInv(); loadTrades();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan trade");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Batalkan trade ini? Stok akan dikembalikan.")) return;
    await api.delete(`/trades/${id}`);
    toast.success("Trade dibatalkan");
    loadInv(); loadTrades();
  };

  return (
    <div>
      <PageHeader title="Trade Kartu" subtitle="Tukar Kartu Buyer / Seller" />

      <div className="rounded-xl border border-slate-800 bg-[#131822] p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <input value={namaLawan} onChange={(e) => setNamaLawan(e.target.value)} placeholder="Nama lawan trade (buyer/seller)" className={inputCls} data-testid="trade-nama-lawan" />
          <input value={kontak} onChange={(e) => setKontak(e.target.value)} placeholder="Kontak (opsional)" className={inputCls} />
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} className={inputCls} data-testid="trade-event">
            <option value="">Di luar event</option>
            {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.nama_event}{ev.status === "active" ? " • Aktif" : ""}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LineEditor title="Kartu Keluar (kita berikan)" lines={keluar} setLines={setKeluar} inventory={inventory} testid="trade-keluar" />
          <LineEditor title="Kartu Masuk (kita terima)" lines={masuk} setLines={setMasuk} inventory={inventory} testid="trade-masuk" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Uang keluar (kita bayar ke lawan)</label>
            <CurrencyInput value={cashKeluar} onChange={setCashKeluar} className={inputCls} data-testid="trade-cash-keluar" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Uang masuk (kita terima dari lawan)</label>
            <CurrencyInput value={cashMasuk} onChange={setCashMasuk} className={inputCls} data-testid="trade-cash-masuk" />
          </div>
        </div>

        <input value={catatan} onChange={(e) => setCatatan(e.target.value)} placeholder="Catatan (opsional)" className={`${inputCls} mt-4`} />

        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-800">
          <div className="flex gap-6 text-sm">
            <div><span className="text-slate-500">Nilai keluar: </span><span className="font-mono text-rose-300">{formatRupiah(nilaiKeluar)}</span></div>
            <div><span className="text-slate-500">Nilai masuk: </span><span className="font-mono text-emerald-300">{formatRupiah(nilaiMasuk)}</span></div>
            <div><span className="text-slate-500">Selisih: </span><span className={`font-mono font-bold ${selisih >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{formatRupiah(selisih)}</span></div>
          </div>
          <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition disabled:opacity-60 shadow-lg shadow-indigo-600/30" data-testid="submit-trade-button">
            <ArrowLeftRight className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Trade"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#131822] overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800"><h3 className="font-heading font-semibold text-white">Riwayat Trade</h3></div>
        {trades.length === 0 ? (
          <EmptyState icon={ArrowLeftRight} title="Belum ada trade" subtitle="Catat pertukaran kartu antar buyer/seller beserta value & top-up uang." />
        ) : (
          <div className="divide-y divide-slate-800/60" data-testid="trades-list">
            {trades.map((t) => (
              <div key={t.id} className="px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-indigo-300">{t.kode}</span>
                    <span className="font-semibold text-white">{t.nama_lawan}</span>
                    {t.event_nama && <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">{t.event_nama}</Badge>}
                    <span className="text-xs text-slate-500">{new Date(t.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "2-digit" })}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-sm font-bold ${t.selisih >= 0 ? "text-emerald-300" : "text-rose-300"}`}>Selisih {formatRupiah(t.selisih)}</span>
                    {isAdmin && <button onClick={() => del(t.id)} data-testid={`delete-trade-${t.id}`} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-rose-300">{t.barang_keluar.map((l) => `${l.nama} ×${l.qty}`).join(", ") || "-"}{t.cash_keluar ? ` + ${formatRupiah(t.cash_keluar)}` : ""}</span>
                  <ArrowRight className="w-4 h-4 text-slate-600" />
                  <span className="text-emerald-300">{t.barang_masuk.map((l) => `${l.nama} ×${l.qty}`).join(", ") || "-"}{t.cash_masuk ? ` + ${formatRupiah(t.cash_masuk)}` : ""}</span>
                </div>
                {t.catatan && <p className="text-xs text-slate-500 mt-1">{t.catatan}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
